import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faCircleCheck, faWifi, faSatelliteDish, faClockRotateLeft,
  faLocationDot, faFileLines, faBolt,
} from '@fortawesome/free-solid-svg-icons';
import './acts.css';
import './Act2_LiveTests.css';

export default function Act2LiveTests({ active }) {
  // 0=idle, 1=payload in flight, 2=asset updating, 3=done
  const [phase, setPhase] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const timers = useRef([]);

  const trigger = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase(0);
    timers.current.push(setTimeout(() => setPhase(1), 80));
    timers.current.push(setTimeout(() => setPhase(2), 1600));
    timers.current.push(setTimeout(() => { setPhase(3); setNow(new Date()); }, 2300));
  }, []);

  useEffect(() => {
    if (!active) return;
    trigger();
    // Auto-loop: restart every 6 seconds so the animation plays continuously
    const interval = setInterval(() => trigger(), 6000);
    return () => {
      clearInterval(interval);
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [active, trigger]);

  const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="act act-2">
      {/* ── Narrative ── */}
      <div className="act-copy">
        <span className="act-eyebrow">
          <span className="act-eyebrow-dot" /> Act 2 · Live tests
        </span>
        <h1 className="act-title">
          Every test, <span className="act-title-accent">live</span>. Zero paperwork.
        </h1>
        <p className="act-lede">
          The moment a tester finishes a pull test in the field, the result streams
          straight into CloudSafe through our Hydrajaws integration. The asset's status
          updates, the next due date is recalculated, and the PDF certificate is
          attached — before the kit is back in the van.
        </p>
        <ul className="act-proofs">
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>No manual data entry.</strong> Results arrive signed from the hardware.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Certificate attached automatically</strong> — one click to open the PDF.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>FMs get notified</strong> the instant a failure is recorded.</span>
          </li>
        </ul>
        <button className="act-cta" onClick={trigger} type="button">
          <FontAwesomeIcon icon={faBolt} />
          <span>Replay the live result</span>
        </button>
      </div>

      {/* ── Stage ── */}
      <div className="act-stage">
        <div className="a2-scene">
          {/* Tester device (left) */}
          <div className="a2-tester">
            <div className="a2-tester-head">
              <FontAwesomeIcon icon={faSatelliteDish} />
              <span>Hydrajaws tester</span>
              <span className="a2-tester-signal" />
            </div>
            <div className="a2-tester-body">
              <div className="a2-tester-label">Peak load</div>
              <div className="a2-tester-load">
                <span className="a2-tester-load-val">6.2</span>
                <span className="a2-tester-load-unit">kN</span>
              </div>
              <div className="a2-tester-status">
                <FontAwesomeIcon icon={faCircleCheck} /> Proof load held · Pass
              </div>
            </div>
            <button
              className={`a2-tester-send ${phase >= 1 ? 'is-sending' : ''}`}
              onClick={trigger}
              type="button"
            >
              {phase === 0 ? 'Send to CloudSafe' : 'Sending…'}
            </button>
          </div>

          {/* Payload in flight */}
          <svg className={`a2-wire ${phase >= 1 ? 'is-live' : ''}`} viewBox="0 0 400 200" preserveAspectRatio="none">
            <defs>
              <linearGradient id="a2-wire-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0d9488" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#0c437c" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            <path
              d="M 0 100 C 120 100, 280 100, 400 100"
              stroke="url(#a2-wire-grad)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="4 6"
              className="a2-wire-path"
            />
            <circle r="6" fill="#0d9488" className="a2-wire-dot">
              <animateMotion
                dur="1.4s"
                repeatCount="1"
                path="M 0 100 C 120 100, 280 100, 400 100"
              />
            </circle>
          </svg>

          {/* Asset card (right) */}
          <div className={`a2-card ${phase >= 2 ? 'is-updating' : ''} ${phase >= 3 ? 'is-done' : ''}`}>
            <div className="a2-card-head">
              <div className="a2-card-ico">
                <FontAwesomeIcon icon={faLocationDot} />
              </div>
              <div>
                <div className="a2-card-id">UCL44-AS-003</div>
                <div className="a2-card-name">Davit Sockets 003</div>
              </div>
              <div className="a2-card-head-right">
                <div className={`a2-card-toast ${phase >= 2 ? 'is-in' : ''}`}>
                  <FontAwesomeIcon icon={faWifi} />
                  <span>Live from tester · Hydrajaws</span>
                </div>
                <span className={`a2-card-badge ${phase >= 3 ? 'is-pass' : 'is-pending'}`}>
                  {phase >= 3 ? (
                    <><FontAwesomeIcon icon={faCircleCheck} /> Compliant</>
                  ) : (
                    <>Awaiting test</>
                  )}
                </span>
              </div>
            </div>

            <div className="a2-card-meta">
              <div className="a2-card-meta-row">
                <span className="a2-card-meta-key">Location</span>
                <span className="a2-card-meta-val">ROOF · Level 4 · North</span>
              </div>
              <div className="a2-card-meta-row">
                <span className="a2-card-meta-key">Last tested</span>
                <span className={`a2-card-meta-val ${phase >= 3 ? 'is-highlighted' : ''}`}>
                  {phase >= 3 ? `${dateStr} · ${timeStr}` : '—'}
                </span>
              </div>
              <div className="a2-card-meta-row">
                <span className="a2-card-meta-key">Next due</span>
                <span className="a2-card-meta-val">
                  {phase >= 3
                    ? new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
                </span>
              </div>
              <div className="a2-card-meta-row">
                <span className="a2-card-meta-key">Proof load</span>
                <span className={`a2-card-meta-val ${phase >= 3 ? 'is-highlighted' : ''}`}>
                  {phase >= 3 ? '6.2 kN' : '—'}
                </span>
              </div>
            </div>

            <div className={`a2-card-cert ${phase >= 3 ? 'is-in' : ''}`}>
              <FontAwesomeIcon icon={faFileLines} />
              <span>Certificate HJ-2026-04-23-UCL44.pdf</span>
              <span className="a2-card-cert-size">148 KB</span>
            </div>

          </div>

          {/* History ribbon below */}
          <div className={`a2-history ${phase >= 3 ? 'is-in' : ''}`}>
            <div className="a2-history-label">
              <FontAwesomeIcon icon={faClockRotateLeft} /> Test history
            </div>
            <div className="a2-history-items">
              <div className="a2-history-item is-new">
                <span className="a2-history-dot is-new" />
                <span className="a2-history-date">{dateStr}</span>
                <span className="a2-history-result is-pass">Pass · 6.2 kN</span>
                <span className="a2-history-new">New</span>
              </div>
              <div className="a2-history-item">
                <span className="a2-history-dot" />
                <span className="a2-history-date">11 Apr 2025</span>
                <span className="a2-history-result is-pass">Pass · 6.0 kN</span>
              </div>
              <div className="a2-history-item">
                <span className="a2-history-dot" />
                <span className="a2-history-date">02 Apr 2024</span>
                <span className="a2-history-result is-pass">Pass · 6.1 kN</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
